import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { describeErrorAndReportIfUnexpected } from 'util/version-mismatch-error-policy';
import { ImageService } from 'services/image-service';
import { UserDb } from 'services/user-db.service';
import { DebugService } from 'services/debug.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { ExerciseDbService } from 'services/exercise-db.service';
import { PayProAPI } from '@cf-shared/paypro-api-model';
import { AuthService, UserAuth } from 'services/auth.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { LoggingService } from 'services/logging.service';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { LL } from 'i18n/i18n.service';
import { OnlineStateService } from 'services/online-state.service';
import { getMyApp } from 'app/app-interface';
import { CouchUserInfo } from '@cf-shared/data-model/couch-user';
import { LoadingController } from 'providers/loading-controller';
import { IHomePage, HomeTab } from 'pages/home/home-interface';
import { IAdminUserPermissionsPage } from 'pages/admin-user-permissions/admin-user-permissions-interface';
import { IAdminUserSubscriptionPage } from 'pages/admin-user-subscription/admin-user-subscription-interface';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { AdminUserSearchPageParams } from './admin-user-search-interface';
import { describeError } from '@cf-shared/error-describe';
import { SearchOperationTracker, TrackedSearchOperation } from 'util/search-operation-tracker';

interface AdminUserSearchUIOption {
  couchUserInfo: CouchUserInfo;  // the info that is stored in our couch database
  payProSubscriptionDetails?: PayProAPI.GetSubscriptionDetailsRequestResponse;  // the info that is stored in paypro}
  selected: boolean;
}

interface SearchOperation extends TrackedSearchOperation {
  foundItems: AdminUserSearchUIOption[];
}

@Component({
    selector: 'page-admin-user-search',
    templateUrl: 'admin-user-search.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ModalPageMarker]
})
export class AdminUserSearchPage extends CsPage<AdminUserSearchPageParams> {
  displayedExercises: AdminUserSearchUIOption[] = [];

  protected prevSearchText = '';
  protected searchText = '';
  protected showSpinner = true;
  protected searchDebouncerTimeout: ReturnType<typeof setTimeout> = undefined;

  protected filteredUsers: AdminUserSearchUIOption[] = [];
  protected sourceOptions: AdminUserSearchUIOption[]; /** used for "copy from..." */

  protected allUsersSelected = false;

  protected numSearchMatchesHidden: number; // number items that match the search but are hidden due to filters
  // Empty means "no error". Holds the wording to show when the lookup itself
  // failed or was refused, which is not the same thing as finding nobody.
  protected searchErrorMessage: TranslatedString = untranslatedString('');

  protected selectedUserId: string = null;

  constructor(
    public navCtrl: NavController,
    protected userDb: UserDb,
    protected imageService: ImageService,
    protected debug: DebugService,
    protected exerciseDb: ExerciseDbService,
    private cd: ChangeDetectorRef,
    private auth: AuthService,
    private cloudFunctions: CloudFunctionsService,
    private alertCtrl: AlertController,
    private loadingCtrl: LoadingController,
    private logging: LoggingService,
    private onlineState: OnlineStateService,
  ) {
    super();
  }

  async ionViewCanEnter() {
  }


  async ionViewDidEnter() {
    let searchText = this.navParams.data.query || '';
    if (searchText) {
      this.searchText = searchText;
      void this.searchAsync(searchText);
    }
  }

  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;

  isSearchInFocus() {
    let inputElement = this.searchInput.nativeElement as HTMLInputElement;
    if (inputElement) {
      return inputElement === document.activeElement;
    }
    return false;
  }

  handleSearchChanged() {
    /* Start showing spinner immediately when user starts typing, even though we start the search only after the debounce
     This makes the UI look more responsive. */
    this.showSpinner = true;
    this.cd.markForCheck();

    // debounce the input typing to prevent too many requests to the databases
    if (this.searchDebouncerTimeout) {
      clearTimeout(this.searchDebouncerTimeout);
    }
    this.prevSearchText = this.searchText;
    let text = this.searchText || '';
    this.searchDebouncerTimeout = setTimeout(() => {
      void this.searchAsync(text);
    }, 300);
  }

  private searchOps = new SearchOperationTracker<SearchOperation>();

  async searchAsync(searchText: string) {
    // separate into two scopes to prevent accidentally using untrimmed searchText
    // This can easily happen due to autocomplete and can cause subtle bugs that are hard to find.
    return this.searchAsync_internal(searchText.trim());
  }

  async searchAsync_internal(trimmedText: string) {
    // if empty search, just clear results and return
    if (!trimmedText) {
      // an emptied box supersedes whatever search is still running, the same way a newer search
      // does. Without this that search is never overtaken, so no cancellation check catches it, and
      // when its answer arrives it writes its rows over the cleared list - leaving the admin looking
      // at a populated table under an empty search box.
      this.searchOps.supersede();
      this.setFilteredUsers([]);
      this.showSpinner = false;
      this.cd.markForCheck();
      return;
    }

    let op = this.searchOps.start(trimmedText, { foundItems: [] });
    if (op === undefined) {
      return;
    }

    this.showSpinner = true;
    this.cd.markForCheck();

    if (this.searchOps.shouldCancel(op)) {
      this.searchOps.end(op);
      return;
    }

    this.sourceOptions = [];
    this.searchErrorMessage = untranslatedString('');
    // hold this search's own result while it waits. Writing it straight into a
    // field would let a search that a newer one has already overtaken leave its
    // answer behind for the newer one to pick up.
    let searchResult: AdminUserSearchUIOption[] = [];
    try {
      let couchUserInfo = await this.auth.obtainCouchUserInfoForUserAsync(trimmedText);
      if (couchUserInfo) {
        let details = await this.cloudFunctions.adminGetUserDetailsAsync({ userId: couchUserInfo.name });
        searchResult = [{ couchUserInfo: couchUserInfo, payProSubscriptionDetails: details.payProSubscriptionDetails, selected: false }];
      }
    } catch (error) {
      // a refusal is not a broken query, so say which one happened. shared
      // policy (wording plus what reaches Sentry): see the helper.
      console.error('Admin search: failed to query user database', error);
      const message = describeErrorAndReportIfUnexpected(
        error,
        this.logging,
        untranslatedString('Failed to query user database'),
      );
      // only the search the admin is actually waiting for gets to speak. A
      // search that has been overtaken must not put its failure on screen: the
      // newer search owns the message now, and may already have succeeded.
      // Reporting to Sentry above is unconditional on purpose — the failure
      // happened whether or not anybody is still waiting for its answer.
      if (!this.searchOps.shouldCancel(op)) {
        this.searchErrorMessage = message;
      }
    }

    // the check at the top of this method can never fire: nothing is awaited
    // between `searchOps.start` claiming the current-search slot and it.
    // This is the one that matters — by now the admin may have typed on and
    // started a newer search, and publishing here would replace that newer
    // search's results with this stale one's. Return without clearing the
    // spinner: the newer search is still running and clears it itself.
    if (this.searchOps.shouldCancel(op)) {
      this.searchOps.end(op);
      return;
    }

    this.sourceOptions = searchResult;
    let foundItems = searchResult;
    let preFilterItems = foundItems;

    op.foundItems = foundItems

    this.setFilteredUsers(op.foundItems);
    this.numSearchMatchesHidden = preFilterItems.length - foundItems.length;
    this.showSpinner = false;
    this.cd.markForCheck();
    this.searchOps.end(op);
  }

  /**
   * The only place `filteredUsers` is written. `selectedUserId` names a row in that list, so the two
   * must be replaced together: the action buttons' `disabled` binding tests the raw id, and a row
   * that has left the list leaves the buttons enabled with `getSelectedUser()` answering `undefined`,
   * which every one of those handlers then dereferences.
   */
  private setFilteredUsers(users: AdminUserSearchUIOption[]) {
    this.filteredUsers = users;
    // if the account the officer picked is still on screen, keep their pick — re-running the same
    // search rebuilds this list and should not silently deselect them.
    if (!users.some(u => u.couchUserInfo.name === this.selectedUserId)) {
      this.selectedUserId = null;
    }
  }

  async ngOnInit() {
    void this.searchAsync(this.searchText);
  }

  async ionViewWillLeave() {

  }

  async ionViewDidLeave() {
  }


  getSelectedItem(): AdminUserSearchUIOption {
    return this.filteredUsers.find(e => e.selected);
  }

  getSelectedUser(): AdminUserSearchUIOption {
    return this.filteredUsers.find(u => u.couchUserInfo.name === this.selectedUserId);
  }

  async startImpersonating(userInfo: CouchUserInfo) {
    // F23 (#3991): impersonation loads the target user's database from the server. Offline it
    // cannot, and it would half-load and then fail with a generic developer-facing error. Say so
    // up front. Untranslated on purpose — this whole page is admin-only, like its other strings.
    // re-check before refusing: the cached state can sit stale for minutes (the foreground tick
    // re-checks an Offline belief, but only every six minutes)
    if (!await this.onlineState.isLikelyOnlineOrRecheckAsync()) {
      await this.alertCtrl.showAlertAsync({
        title: untranslatedString('You\'re Offline'),
        message: untranslatedString('Impersonation needs an internet connection.'),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
      });
      return;
    }

    let loader = this.loadingCtrl.create({
      content: untranslatedString('Switching to impersonated user profile data. Please wait...'),
      // loadImpersonatedUserDataAsync cursor-paginates through downloadUserDb;
      // a large user DB can take minutes. Keep the screen awake so iOS doesn't
      // auto-lock and suspend the in-flight fetches mid-pagination.
      keepScreenAwake: true,
    });
    await loader.present();
    let impersonatingUserAuth: UserAuth = {
      name: userInfo.name,
      email: userInfo.email,
      uid: userInfo.name,
      avatarUrl: this.auth.user.avatarUrl,
      method: 'impersonate',
    };
    // an officer already impersonating someone can start another impersonation from here
    const previousImpersonation = this.userDb.getImpersonationUserAuth();
    this.userDb.setImpersonationUserAuth(impersonatingUserAuth);
    try {
      await this.userDb.loadImpersonatedUserDataAsync();
    } catch (err) {
      // do not navigate into a half-loaded session — roll back impersonation
      // state and the partial PouchDB so the admin can try again or pick a
      // different user. Surface to Sentry: any failure here blocks support
      // work and we want a trail. dismiss in a finally so the spinner and its
      // wake-lock are released even if the rollback itself throws — otherwise
      // the screen would be held awake until the app is killed.
      // #6448: the rollback never changes where this session writes; it puts back the
      // impersonation that was running (if any) and deletes only the partial download
      try {
        this.logging.trackAbnormalException(err);
        this.userDb.setImpersonationUserAuth(previousImpersonation);
        await this.userDb.discardFailedImpersonationDownloadAsync();
      } finally {
        await loader.dismiss();
      }
      const message = describeError(err);
      void this.alertCtrl.showAlertAsync({
        message: untranslatedString(`Could not load user data for impersonation: ${message}`),
      });
      return;
    }
    // must reload to login again to get the new features
    void this.navCtrl.setRootAsync(IHomePage, { tab: HomeTab.DailyLog }).then(() => {
      void getMyApp().reloadAppAsync({ resetToRoot: false });
    });

    await loader.dismiss();
  }

  handlePermissionsButtonClick(user: AdminUserSearchUIOption) {
    void this.navCtrl.pushAsync(IAdminUserPermissionsPage, { email: user.couchUserInfo.email });
  }

  handleImpersonateButtonClick(user: AdminUserSearchUIOption) {
    if (user.couchUserInfo.name == this.auth.couch.user) {
      void this.alertCtrl.showAlertAsync({ message: untranslatedString("You can't impersonate yourself") }); // TODO: admin interface
      return;
    }
    else {
      void this.startImpersonating(user.couchUserInfo);
    }
  }
  handleSubscriptionButtonClick(user: AdminUserSearchUIOption) {
    void this.navCtrl.pushAsync(IAdminUserSubscriptionPage, { id: user.couchUserInfo.name });
  }

  async handleCoachSlotsButtonClick(user: AdminUserSearchUIOption) {
    const maxSlots = await this.alertCtrl.showAlertAsync<number>({
      title: untranslatedString('Set Coach Slots'),
      message: untranslatedString(`Set max client slots for ${user.couchUserInfo.email}`),
      inputs: [{ name: 'maxSlots', type: 'number', placeholder: untranslatedString('Number of slots'), value: '5' }],
      buttons: [
        { text: untranslatedString('Cancel'), role: AlertButtonRole.Cancel, result: null },
        { text: untranslatedString('Set'), role: AlertButtonRole.Primary, result: (data: any) => parseInt(data.maxSlots, 10) },
      ],
    });
    if (maxSlots == null || isNaN(maxSlots)) return;

    try {
      await this.cloudFunctions.adminSetCoachSlotsAsync({ targetUid: user.couchUserInfo.name, maxSlots });
      // When the admin sets THEIR OWN slots, patch the in-memory `auth.couch.coachMaxSlots`
      // so Settings → Coaching becomes visible on the next change-detection tick (e.g. when
      // the user navigates back). Without this, the gating reads stale 0 from couchLogin
      // and the user has to reload — confusing UX. `_users` isn't synced to the client so
      // a DocWatcher can't pick this up; in-memory patch is the cleanest fix.
      if (user.couchUserInfo.name === this.auth.user?.uid && this.auth.couch) {
        this.auth.couch.coachMaxSlots = maxSlots;
      }
      void this.alertCtrl.showAlertAsync({ message: untranslatedString(`Coach slots set to ${maxSlots}`) });
    } catch (error) {
      // adminSetCoachSlots reads the target account's `_users` doc on the server,
      // so since #4283 this catch can see the "this server is behind that
      // account's data" refusal (HTTP 409). Route it through the shared policy:
      // that condition gets its own honest wording and stays out of Sentry, while
      // anything else is now actually REPORTED — before this, every failure here
      // showed a bare message and nobody was ever told.
      void this.alertCtrl.showAlertAsync({
        message: describeErrorAndReportIfUnexpected(
          error,
          this.logging,
          untranslatedString(`Failed: ${describeError(error)}`),
        ),
      });
    }
  }
}
